/**
 * Global Tabs Component
 *
 * Manages global tab settings and configuration.
 *
 * @since 1.0.0
 */

import { ArrowPathIcon, ArrowUturnLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { colors } from '@redfoxwp/core-components/colors';
import { Button } from '@redfoxwp/core-components/components/button';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { Modal, type ModalStrings } from '@redfoxwp/core-components/components/modal';
import { TabHeader } from '@redfoxwp/core-components/components/tab-header';
import { __ } from '@wordpress/i18n';
import { useEffect, useState } from 'react';

import { GlobalTabsProvider, useGlobalTabsContext } from '../../contexts';
import { setUnsavedChanges } from '../../utils';
import { GlobalTabsManager } from './components/global-tabs-manager';

/**
 * Global Tabs content component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
const GlobalTabsContent = (): JSX.Element => {
  const { isSaving, hasUnsavedChanges, saveTabs, resetTabs, resetToDefaults } =
    useGlobalTabsContext();

  // Modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [showResetToDefaultsModal, setShowResetToDefaultsModal] = useState(false);

  const handleSave = async () => {
    await saveTabs();
  };

  const handleReset = () => {
    setShowResetModal(true);
  };

  const handleResetToDefaults = () => {
    setShowResetToDefaultsModal(true);
  };

  const confirmReset = () => {
    resetTabs();
    setShowResetModal(false);
  };

  const confirmResetToDefaults = async () => {
    await resetToDefaults();
    setShowResetToDefaultsModal(false);
  };

  // Sync unsaved changes state with global navigation guard
  useEffect(() => {
    setUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges]);

  // Clean up navigation guard when component unmounts
  useEffect(() => {
    return () => {
      setUnsavedChanges(false);
    };
  }, []);

  // Browser warning when leaving WordPress admin entirely (not for route changes)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        const message = __(
          'You have unsaved changes. Are you sure you want to leave?',
          'redfox-tab-manager'
        );
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  return (
    <>
      <Flex direction='column' gap='30px' fullWidth>
        <TabHeader
          title={__('Global Tabs', 'redfox-tab-manager')}
          description={__(
            'Configure global tab settings that apply to all products. You can enable/disable tabs, set their order, and manage default WooCommerce tabs.',
            'redfox-tab-manager'
          )}
        />

        <Card styleProps={{ padding: '30px', radius: '10px', background: '#fff' }}>
          <GlobalTabsManager />
        </Card>

        <Card styleProps={{ padding: '30px', radius: '10px', background: '#fff' }}>
          <Flex justify='space-between' align='center'>
            <Button
              onClick={handleResetToDefaults}
              variant='outline'
              size='small'
              disabled={isSaving}
              styleProps={{
                borderColor: colors?.gray?.[300],
                color: colors?.gray?.[500],
                hoverBackgroundColor: colors?.gray?.[100],
                hoverColor: colors?.gray?.[700],
                hoverBorderColor: colors?.gray?.[300],
              }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              {__('Reset to Defaults', 'redfox-tab-manager')}
            </Button>
            <Flex gap='15px'>
              <Button
                onClick={handleReset}
                variant='outline'
                disabled={isSaving || !hasUnsavedChanges}
              >
                <ArrowUturnLeftIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                {__('Reset Changes', 'redfox-tab-manager')}
              </Button>
              <Button
                onClick={handleSave}
                variant='solid'
                disabled={isSaving || !hasUnsavedChanges}
              >
                {!isSaving && (
                  <CheckIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                )}
                {isSaving ? __('Saving…', 'redfox-tab-manager') : __('Save', 'redfox-tab-manager')}
              </Button>
            </Flex>
          </Flex>
        </Card>

        {/* Create localized strings for the modals */}
        {(() => {
          const modalStrings: ModalStrings = {
            closeModal: __('Close modal', 'redfox-tab-manager'),
          };

          return (
            <>
              {/* Reset Changes Confirmation Modal */}
              <Modal
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                title={__('Reset Changes', 'redfox-tab-manager')}
                strings={modalStrings}
                footer={
                  <Flex justify='flex-end' gap='15px'>
                    <Button variant='outline' onClick={() => setShowResetModal(false)}>
                      {__('Cancel', 'redfox-tab-manager')}
                    </Button>
                    <Button variant='solid' onClick={confirmReset}>
                      {__('Reset', 'redfox-tab-manager')}
                    </Button>
                  </Flex>
                }
              >
                <p>
                  {__(
                    'Are you sure you want to reset all changes? This will restore the tabs to their last saved state.',
                    'redfox-tab-manager'
                  )}
                </p>
              </Modal>

              {/* Reset to Defaults Confirmation Modal */}
              <Modal
                isOpen={showResetToDefaultsModal}
                onClose={() => setShowResetToDefaultsModal(false)}
                title={__('Reset to Defaults', 'redfox-tab-manager')}
                strings={modalStrings}
                footer={
                  <Flex justify='flex-end' gap='15px'>
                    <Button variant='outline' onClick={() => setShowResetToDefaultsModal(false)}>
                      {__('Cancel', 'redfox-tab-manager')}
                    </Button>
                    <Button variant='solid' onClick={confirmResetToDefaults}>
                      {__('Reset to Defaults', 'redfox-tab-manager')}
                    </Button>
                  </Flex>
                }
              >
                <p>
                  {__(
                    'Are you sure you want to reset ALL tabs to default WooCommerce tabs? This will permanently delete all custom tabs and cannot be undone.',
                    'redfox-tab-manager'
                  )}
                </p>
              </Modal>
            </>
          );
        })()}
      </Flex>
    </>
  );
};

/**
 * Global Tabs component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const GlobalTabs = (): JSX.Element => {
  return (
    <GlobalTabsProvider>
      <GlobalTabsContent />
    </GlobalTabsProvider>
  );
};
